import type { ThresholdConfig } from '@/store/api/dataLayersApi';
import type { Threshold } from '../types';

/**
 * Normalizes ui_threshold from metadata to a consistent array format
 * Handles both legacy number format and new array format
 *
 * Why this is needed:
 * - Backend sends ui_threshold in two formats:
 *   1. Legacy: single number (e.g., 100.0)
 *   2. New: array of threshold objects with id, value, label, color
 * - We normalize to always work with arrays for consistent processing
 *
 * @param ui_threshold - Raw threshold from metadata (number or array or null)
 * @returns Array of threshold configs (empty if no threshold)
 */
export function normalizeMetadataThreshold(
  ui_threshold: number | ThresholdConfig[] | null | undefined
): ThresholdConfig[] {
  if (!ui_threshold) return [];

  // Handle legacy number format - convert to array with single threshold
  if (typeof ui_threshold === 'number') {
    return [
      {
        id: 'threshold_1',
        x: ui_threshold,
        label: 'Threshold',
        color: '#CC0000',
        type: 'default',
      },
    ];
  }

  // Handle new array format - pass through as-is
  if (Array.isArray(ui_threshold)) {
    return ui_threshold;
  }

  return [];
}

/**
 * Transforms metadata threshold configs to chart threshold format
 *
 * Why transformation is needed:
 * - Metadata format (ThresholdConfig) is missing required chart fields:
 *   - sensorName: needed to associate threshold with specific sensor
 *   - visible: needed for show/hide toggle functionality
 *   - dash: needed for line style (dotted, dashed, etc.)
 * - Some metadata fields are optional but required for charts:
 *   - color: defaults to red (#CC0000) if not provided
 *   - label: defaults to threshold id or "Threshold X"
 * - Field naming differs: metadata 'type' → chart 'chartType'
 *
 * @param sensorName - Name of sensor to associate thresholds with
 * @param metadataThresholds - Normalized threshold configs from metadata
 * @returns Array of chart-compatible threshold objects
 */
export function transformMetadataThresholdToChartFormat(
  sensorName: string,
  metadataThresholds: ThresholdConfig[]
): Partial<Threshold>[] {
  return metadataThresholds.map((threshold, index) => ({
    // Create unique ID by combining sensor name with threshold id
    id: `${sensorName}-${threshold.id || `threshold-${index}`}`,
    // Associate threshold with specific sensor (required for filtering in split mode)
    sensorName,
    // Y-axis value where line will be drawn
    value: threshold.x,
    // Default to red if color not specified
    color: threshold.color || '#CC0000',
    // Use provided label or fallback to id or generic label
    label: threshold.label || threshold.id || `Threshold ${index + 1}`,
    // Always visible by default (user can toggle later)
    visible: true,
    // Map 'type' field to 'chartType'
    chartType: threshold.type || 'default',
    // Use dotted line style for all thresholds
    dash: 'dot' as const,
  }));
}

/**
 * Merges metadata thresholds with existing data thresholds
 * Data thresholds take precedence if they exist
 *
 * Priority logic:
 * - Data API thresholds have highest priority (if present, ignore metadata)
 * - Metadata thresholds are fallback when data doesn't provide any
 * - This ensures backward compatibility with existing data sources
 *
 * @param dataThresholds - Thresholds from sensor data API response
 * @param metadataThresholds - Thresholds from metadata legend items
 * @returns Final thresholds to use (data takes precedence)
 */
export function mergeThresholds(
  dataThresholds: Partial<Threshold>[] | undefined,
  metadataThresholds: Partial<Threshold>[]
): Partial<Threshold>[] {
  // If data already has thresholds, use them (they take precedence)
  if (dataThresholds && dataThresholds.length > 0) {
    return dataThresholds;
  }

  // Otherwise use metadata thresholds as fallback
  return metadataThresholds;
}
